import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getProcessingRemarkCategory, getRiskRemarkCategory } from './documentationCategories.js';
import { FORGERY_TESTS } from '../data/forgeryTests.js';

// Lightweight mapping for data source display in the DocumentData2 table.
// Add more mappings here if you use other source keys in metadata.documentData2DataSource
const dataSourceMap = {
  sessionResult: 'Session Result',
  idvResultData: 'IDV Result Data',
  resultData: 'Result Data',
  root: 'Root'
};

const formatDocumentDataValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map(item => formatDocumentDataValue(item))
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') {
    if ('Value' in value) {
      return formatDocumentDataValue(value.Value);
    }
    if ('RawData' in value && value.RawData && 'Value' in value.RawData) {
      return formatDocumentDataValue(value.RawData.Value);
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

const flattenDocumentDataField = (fieldKey, value, basePath) => {
  if (value === null || value === undefined) return [];
  const safePath = basePath || fieldKey;

  if (typeof value !== 'object' || value instanceof Date) {
    return [[fieldKey, value, safePath]];
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    return value.flatMap((item, idx) => {
      const childKey = `${fieldKey}[${idx}]`;
      const childPath = `${safePath}[${idx}]`;
      return flattenDocumentDataField(childKey, item, childPath);
    });
  }

  if ('Value' in value && value.Value !== undefined) {
    const valPath = `${safePath}.Value`;
    return [[fieldKey, value.Value, valPath]];
  }

  if (
    'RawData' in value &&
    value.RawData &&
    typeof value.RawData === 'object' &&
    'Value' in value.RawData
  ) {
    const rawPath = `${safePath}.RawData.Value`;
    return [[fieldKey, value.RawData.Value, rawPath]];
  }

  const nestedEntries = Object.entries(value).flatMap(([nestedKey, nestedVal]) => {
    const childKey = `${fieldKey}.${nestedKey}`;
    const childPath = `${safePath}.${nestedKey}`;
    return flattenDocumentDataField(childKey, nestedVal, childPath);
  });

  return nestedEntries.length > 0 ? nestedEntries : [[fieldKey, value, safePath]];
};

const resolveNestedMetaValue = (collection, key) => {
  if (!collection) return '';
  if (collection[key] !== undefined) return collection[key];
  const rootKey = key.split('.')[0].split('[')[0];
  return collection[rootKey] !== undefined ? collection[rootKey] : '';
};

const InfoTooltip = React.memo(({ path, additionalInfo, category, className = '' }) => {
  const ref = useRef(null);
  const titleText = `JSON Path: ${path || 'N/A'}${additionalInfo ? `\n\n${additionalInfo}` : ''}${category ? `\n\nCategory: ${category}` : ''}`;

  const showCopyFeedback = useCallback(() => {
    const el = ref.current;
    if (
      !el ||
      typeof window === 'undefined' ||
      !window.bootstrap ||
      !window.bootstrap.Tooltip
    ) {
      return;
    }
    let instance = window.bootstrap.Tooltip.getInstance(el);
    if (!instance) {
      instance = new window.bootstrap.Tooltip(el);
    }
    const original = el.getAttribute('data-bs-original-title') || el.getAttribute('title') || titleText;
    el.setAttribute('data-bs-original-title', 'Copied!');
    el.setAttribute('title', 'Copied!');
    instance.show();
    setTimeout(() => {
      instance.hide();
      el.setAttribute('data-bs-original-title', original);
      el.setAttribute('title', original);
    }, 900);
  }, [titleText]);

  const fallbackCopy = useCallback(() => {
    if (!path || typeof document === 'undefined') {
      throw new Error('Clipboard not available');
    }
    const ta = document.createElement('textarea');
    ta.value = path;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const success = document.execCommand && document.execCommand('copy');
    document.body.removeChild(ta);
    if (!success) {
      throw new Error('Fallback copy failed');
    }
    return true;
  }, [path]);

  const copyToClipboard = useCallback(async () => {
    if (!path) return;
    const canUseNavigatorClipboard =
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      (typeof window === 'undefined' || window.isSecureContext !== false);
    try {
      if (canUseNavigatorClipboard) {
        await navigator.clipboard.writeText(path);
      } else {
        fallbackCopy();
      }
      showCopyFeedback();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('Async clipboard copy failed, falling back:', err);
      try {
        fallbackCopy();
        showCopyFeedback();
      } catch (fallbackError) {
        if (process.env.NODE_ENV !== 'production') console.warn('Clipboard copy failed:', fallbackError);
      }
    }
  }, [fallbackCopy, path, showCopyFeedback]);

    const tooltipClassName = ['doc-info-tooltip', 'ms-2', className].filter(Boolean).join(' ').trim();

    return (
      <span
        ref={ref}
        className={tooltipClassName}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-bs-custom-class="tooltip-white"
        title={titleText}
      role="button"
      onClick={copyToClipboard}
      onKeyDown={(event) => {
        if (!path) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          copyToClipboard();
        }
      }}
      tabIndex={path ? 0 : -1}
      aria-label={path ? `Copy JSON path ${path}` : 'Info'}
    >
      <i className="bi bi-info-circle"></i>
    </span>
  );
});

const ResultCard = React.memo(({ title, value, path, className = '', additionalInfo = '', category = '' }) => {
  const displayValue = value !== undefined && value !== null && value !== ''
    ? value
    : 'No value available';

  return (
    <motion.div
      className={`card glass-card elevate mb-3 ${className}`}
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.25 }}
    >
      <div className="card-body">
        <h5 className="card-title d-flex align-items-center">
          {title || 'Unknown Field'}
          {path && <InfoTooltip path={path} additionalInfo={additionalInfo} category={category} />}
        </h5>
        <p className="card-text mb-1">{displayValue}</p>
        {additionalInfo && (
          <p className="card-subtext text-muted small mb-0">{additionalInfo}</p>
        )}
      </div>
    </motion.div>
  );
});

const RemarksList = ({ remarks, title, type, category, onShowDocumentation }) => {
  const [openCategories, setOpenCategories] = useState({});

  const getRemarkCategory = (remark) => {
    if (remark && remark.category) return remark.category;
    const code = remark?.code;
    if (code === undefined || code === null) return 'Other';
    if (type === 'risk') {
      return getRiskRemarkCategory(code);
    }
    if (type === 'processing') {
      return getProcessingRemarkCategory(code);
    }
    return 'Other';
  };

  if (!remarks || !Array.isArray(remarks)) {
    return (
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">{title || 'Remarks'}</h5>
          <p className="text-muted">No remarks data available</p>
        </div>
      </div>
    );
  }

    const categorizedRemarks = remarks.reduce((acc, remark) => {
      if (!remark || typeof remark.code === 'undefined') return acc;

      const category = getRemarkCategory(remark);
      if (!acc[category]) acc[category] = [];
      acc[category].push({ ...remark, category });
      return acc;
    }, {});

  const handleShowDocumentation = () => {
    if (onShowDocumentation && type) {
      onShowDocumentation(type);
    }
  };

  const handleToggleCategory = (category) => {
    setOpenCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleOpenAll = () => {
    const allOpen = {};
    Object.keys(categorizedRemarks).forEach((cat) => { allOpen[cat] = true; });
    setOpenCategories(allOpen);
  };

  const handleCloseAll = () => {
    setOpenCategories({});
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="remarks-card-header d-flex justify-content-between align-items-center flex-wrap">
          <div className="d-flex align-items-center flex-grow-1 gap-1" style={{ minWidth: 0 }}>
            <h5 className="card-title d-flex align-items-center mb-0" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {title || 'Remarks'}
              {/* Add InfoTooltip next to the title for both types */}
              {remarks[0]?.path && (
                (() => {
                  const firstPath = remarks[0].path || '';
                  return <InfoTooltip path={firstPath} />;
                })()
              )}
            </h5>
          </div>
          <div className="remarks-card-actions d-flex align-items-center flex-shrink-0 ms-2">
            <button className="btn btn-sm btn-outline-secondary me-1" onClick={handleOpenAll} type="button">
              <i className="bi bi-arrows-angle-expand"></i> Open all
            </button>
            <button className="btn btn-sm btn-outline-secondary me-2" onClick={handleCloseAll} type="button">
              <i className="bi bi-arrows-angle-contract"></i> Close all
            </button>
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={handleShowDocumentation}
            >
              <i className="bi bi-book"></i> View Remark Codes
            </button>
          </div>
        </div>
        {remarks.length > 0 && (
          <div className="mb-2">
            {/* Remove InfoTooltip from here, now shown next to the title */}
          </div>
        )}
        {remarks.length > 0 ? (
          <div className="accordion" id={`${type}Accordion`}>
            {Object.entries(categorizedRemarks).map(([category, categoryRemarks], index) => {
              const isOpen = !!openCategories[category];
              return (
                <div key={category} className="accordion-item">
                  <h2 className="accordion-header" id={`${type}Heading${index}`}> 
                    <button
                      className={`accordion-button${isOpen ? '' : ' collapsed'}`}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={`${type}Collapse${index}`}
                      onClick={() => handleToggleCategory(category)}
                    >
                      {category} ({categoryRemarks.length})
                    </button>
                  </h2>
                  <div
                    id={`${type}Collapse${index}`}
                    className={`accordion-collapse collapse${isOpen ? ' show' : ''}`}
                    aria-labelledby={`${type}Heading${index}`}
                    data-bs-parent={null}
                  >
                    <div className="accordion-body p-0">
                      <ul className="list-group list-group-flush">
                        {categoryRemarks.map((remark, idx) => (
                            <li key={idx} className="list-group-item">
                              <div className="doc-remark-row">
                                <div className="doc-remark-message">
                                  <span className="doc-remark-text">
                                    {remark.message || `Remark ${remark.code}`}
                                  </span>
                                  {remark.path && (
                                    <InfoTooltip
                                      path={remark.path}
                                      category={remark.category}
                                      className="doc-remark-tooltip"
                                    />
                                  )}
                                </div>
                                <span className="badge bg-secondary doc-remark-code">{remark.code}</span>
                              </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted">No {type} remarks found</p>
        )}
      </div>
    </div>
  );
};

const ResultsDisplay = ({ data, onShowDocumentation }) => {
  const [activeForgeryDetail, setActiveForgeryDetail] = useState(null);

  if (!data || typeof data !== 'object' || !data.summary) {
    return (
      <div className="alert alert-danger" role="alert">
        Invalid or incomplete data format received
      </div>
    );
  }

  // Safely extract data with fallbacks
  const primaryResult = data.summary?.primaryResult || 'No primary result available';
  const completionStatus = data.summary?.completionStatus || 'No completion status available';
  const failureReason = data.summary?.failureReason;
  const processingRemarks = data.remarks?.processing || [];
  const riskManagementRemarks = data.remarks?.riskManagement || [];
  const metadata = data.metadata || {};
  const paths = data.paths || {};
  const jsonType = metadata.jsonType || '';
  const workflowNumber = metadata.workflowNumber || '';
  const workflowShort = workflowNumber ? workflowNumber.replace(/^Au10tix/, '') : '';
  const forgeryTests = Array.isArray(data.forgeryTests) ? data.forgeryTests : [];
  const hasProcessingRemark20 = processingRemarks.some(remark => Number(remark.code) === 20);

  const formatForgeryLocation = (value) => {
    if (value === null || value === undefined) return 'Unknown';
    const labels = ['Front', 'Back', 'Selfie'];
    return labels[value] || `Location ${value}`;
  };

  const isNonAuthenticForgeryTest = (test) => {
    const result = test?.testResult;
    if (!result) return false;
    return String(result).toLowerCase() !== 'authentic';
  };

  const flaggedForgeryTests = forgeryTests.filter(isNonAuthenticForgeryTest);
  const shouldShowForgerySection = hasProcessingRemark20 && flaggedForgeryTests.length > 0;
  const findForgeryDocEntry = (test) => {
    const candidates = [
      test?.subtype?.trim().toLowerCase(),
      test?.type?.trim().toLowerCase()
    ].filter(Boolean);
    if (candidates.length === 0) return null;
    return FORGERY_TESTS.find(entry => {
      const normalizedName = entry.name.toLowerCase();
      return candidates.some(candidate => normalizedName === candidate);
    }) || FORGERY_TESTS.find(entry => {
      const normalizedName = entry.name.toLowerCase();
      return candidates.some(candidate => normalizedName.includes(candidate));
    }) || null;
  };

  const handleForgeryCardSelect = (test) => {
    const docEntry = findForgeryDocEntry(test);
    setActiveForgeryDetail({
      title: test.subtype || test.type || 'Forgery Test',
      location: formatForgeryLocation(test.location),
      type: test.type || '',
      description: docEntry?.description || 'Description not available in documentation.',
      sourceName: docEntry?.name || test.subtype || test.type || null
    });
  };

  const closeForgeryDetail = () => setActiveForgeryDetail(null);
  const handleViewInDocs = () => {
    closeForgeryDetail();
    if (onShowDocumentation) {
      onShowDocumentation('forgery');
    }
  };

  const shouldShowTestResult = (result) => {
    if (!result) return false;
    return String(result).toLowerCase() !== 'authentic';
  };

  const getTestResultBadgeTone = (result) => {
    if (!result) return 'text-bg-secondary';
    const normalized = String(result).toLowerCase();
    if (normalized.includes('forg') || normalized.includes('fail')) {
      return 'text-bg-danger';
    }
    if (normalized.includes('review') || normalized.includes('warn') || normalized.includes('manual')) {
      return 'text-bg-warning';
    }
    if (normalized.includes('inconclusive') || normalized.includes('indecisive')) {
      return 'text-bg-warning';
    }
    if (normalized.includes('incomplete')) {
      return 'text-bg-info';
    }
    return 'text-bg-secondary';
  };

  const getForgeryStatusLabel = (result) => {
    const normalized = (result || '').toLowerCase();
    if (normalized.includes('incomplete')) {
      return { label: 'InComplete', icon: 'bi-slash-circle', className: 'text-info' };
    }
    if (normalized.includes('inconclusive') || normalized.includes('indecisive')) {
      return { label: 'Indecisive', icon: 'bi-question-circle', className: 'text-warning' };
    }
    return { label: 'Forged', icon: 'bi-exclamation-triangle', className: 'text-danger' };
  };

  const getStatusColor = (status) => {
    if (!status) return 'secondary';
    
    switch (status.toLowerCase()) {
      case 'passed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'inconclusive':
        return 'warning';
      case 'incomplete':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getStatusInfo = (status) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
      case 'passed':
        return 'Document passed all verification checks';
      case 'failed':
        return 'Document failed one or more verification checks';
      case 'inconclusive':
        return 'Verification results are inconclusive';
      case 'incomplete':
        return 'Verification process was not completed';
      default:
        return '';
    }
  };

  const handleShowDocumentation = (type) => {
    if (onShowDocumentation && type) {
      onShowDocumentation(type);
    }
  };

  // Enhanced metadata display
  const renderMetadata = () => {
    const fieldPaths = metadata.fieldPaths || {};
    const standardFields = [];
    let documentData2Node = null;

    const pushField = (key, label, value, path) => {
      if (value === undefined || value === null || value === '') return;
      const displayValue = Array.isArray(value)
        ? value.join(', ')
        : (typeof value === 'boolean'
          ? (value ? 'Yes' : 'No')
          : (typeof value === 'object'
            ? JSON.stringify(value)
            : value));

      standardFields.push(
        <div key={key} className="metadata-field d-flex align-items-center justify-content-between flex-wrap">
          <span className="metadata-field-label">{label}</span>
          <span className="metadata-field-value d-inline-flex align-items-center gap-2">
            {displayValue}
            {path ? <InfoTooltip path={path} /> : null}
          </span>
        </div>
      );
    };

    // Basic metadata
    if (metadata.processedAt) {
      pushField(
        'processedAt',
        'Processed At',
        new Date(metadata.processedAt).toLocaleString(),
        fieldPaths.processedAt
      );
    }
    if (metadata.jsonType) {
      pushField('jsonType', 'JSON Type', metadata.jsonType, fieldPaths.jsonType);
    }
    // Document-specific metadata
    // Only show Document Type once if both are present and equal
    const docType = metadata.documentType;
    const docTypePath = fieldPaths.documentType;
    const docTypeDesc = metadata.documentTypeDescriptorType;
    const docTypeDescPath = fieldPaths.documentTypeDescriptorType;
    if (docType && docTypeDesc) {
      if (docType === docTypeDesc) {
        pushField('documentType', 'Document Type', docType, docTypeDescPath || docTypePath);
      } else {
        pushField('documentType', 'Document Type', docType, docTypePath);
        pushField('docTypeDescType', 'Document Type (Descriptor)', docTypeDesc, docTypeDescPath);
      }
    } else if (docType) {
      pushField('documentType', 'Document Type', docType, docTypePath);
    } else if (docTypeDesc) {
      pushField('docTypeDescType', 'Document Type', docTypeDesc, docTypeDescPath);
    }
    pushField('country', 'Country', metadata.country, fieldPaths.country);
    pushField('requestId', 'Request ID', metadata.requestId, fieldPaths.requestId);
    pushField('processingDate', 'Processing Date', metadata.processingDate, fieldPaths.processingDate);
    pushField('documentStatus', 'Document Status', metadata.documentStatus, fieldPaths.documentStatus);
    pushField(
      'docTypeDescCountryIso3',
      'Document Country (ISO3)',
      metadata.documentTypeDescriptorCountryIso3,
      fieldPaths.documentTypeDescriptorCountryIso3
    );
    pushField(
      'docTypeDescVersion',
      'Document Version',
      metadata.documentTypeDescriptorVersion,
      fieldPaths.documentTypeDescriptorVersion
    );
    // DocumentData2 fields table (unchanged)
    if (metadata.documentData2Fields && Object.keys(metadata.documentData2Fields).length > 0) {
      const docData2Rows = Object.entries(metadata.documentData2Fields)
        .filter(([, v]) => v !== null && v !== undefined)
        .flatMap(([k, v]) => flattenDocumentDataField(
          k,
          v,
          metadata.documentData2Paths && metadata.documentData2Paths[k]
        ));
      documentData2Node = (
        <div key="docdata2" className="metadata-docdata2 mt-4">
          <h6 className="text-primary">Extracted Document Data (DocumentData2)</h6>
          {metadata.documentData2Source && (
            <p className="text-muted small mb-2">Source: <code>{metadata.documentData2Source}</code></p>
          )}
          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle text-nowrap" style={{ fontSize: '0.92em', tableLayout: 'auto', width: '100%' }}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                  <th>JSON Path</th>
                  <th>Data Source</th>
                  <th>Data Source Path</th>
                  <th>Info</th>
                </tr>
              </thead>
              <tbody>
                {docData2Rows.map(([k, v, path]) => {
                  const formattedValue = formatDocumentDataValue(v);
                  const isMultilineValue = formattedValue.includes('\n');
                  const dataSourceValue = resolveNestedMetaValue(metadata.documentData2DataSource, k);
                  const dataSourcePath = resolveNestedMetaValue(metadata.documentData2DataSourcePaths, k);
                  const compareValue = resolveNestedMetaValue(metadata.documentData2Compare, k);
                  return (
                    <tr key={k}>
                      <td>{k}</td>
                      <td>
                        {isMultilineValue ? (
                          <pre className="docdata-preformatted">{formattedValue}</pre>
                        ) : formattedValue}
                      </td>
                      <td><code>{path || (metadata.documentData2Paths && metadata.documentData2Paths[k])}</code></td>
                      <td>{dataSourceValue !== '' ? (dataSourceMap[dataSourceValue] || dataSourceValue) : ''}</td>
                      <td><code>{dataSourcePath || ''}</code></td>
                      <td>{compareValue || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Additional metadata fields
    ['version', 'source', 'timestamp', 'createdat', 'updatedat'].forEach(field => {
      pushField(
        field,
        field.charAt(0).toUpperCase() + field.slice(1),
        metadata[field],
        fieldPaths[field]
      );
    });

    const nodes = [];
    if (standardFields.length > 0) {
      nodes.push(
        <div key="metadata-fields" className="metadata-grid">
          {standardFields}
        </div>
      );
    } else if (!documentData2Node) {
      nodes.push(<p key="metadata-empty" className="text-muted">No metadata available</p>);
    }
    if (documentData2Node) {
      nodes.push(documentData2Node);
    }
    return nodes.length === 1 ? nodes[0] : nodes;
  };

  return (
    <div className="results-display">
      {/* Documentation Button */}
      <div className="d-flex justify-content-end mb-2">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onShowDocumentation ? onShowDocumentation('processing') : null}
        >
          <i className="bi bi-book me-1"></i> Remark Codes
        </button>
      </div>
      {/* JSON Type and Workflow Number near Summary */}
      {(jsonType || workflowNumber) && (
        <div className="json-type-container-workflow" aria-live="polite">
          {jsonType && (
            <span
              className="json-type-badge "
              role="note"
              aria-label={`JSON type ${jsonType}`}
            >
              <i className="bi bi-braces-asterisk me-1" aria-hidden="true"></i>
              JSON Type: {jsonType}
            </span>
          )}
          {workflowNumber && (
            <span
              className="workflow-badge"
              role="note"
              aria-label={`Workflow ${workflowShort || workflowNumber}`}
            >
              <i className="bi bi-diagram-3 me-1" aria-hidden="true"></i>
              Workflow: {workflowShort || workflowNumber}
            </span>
          )}
        </div>
      )}
      {/* Summary Section */}
      <motion.div className="summary-section mb-4" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
        <h3 className="section-title">Summary</h3>
        <div className="row">
          <div className="col-md-6">
            <ResultCard
              title="Primary Result"
              value={primaryResult}
              path={paths.primaryResult}
              className={`border-${getStatusColor(primaryResult)}`}
              additionalInfo={getStatusInfo(primaryResult)}
            />
          </div>
          <div className="col-md-6">
            <ResultCard
              title="Completion Status"
              value={completionStatus}
              path={paths.completionStatus}
            />
          </div>
        </div>
        {failureReason && (
          <ResultCard
            title="Failure Reason"
            value={failureReason}
            path={paths.failureReason}
            className="border-danger"
          />
        )}
      </motion.div>

      {shouldShowForgerySection && (
        <div className="row g-3">
          <motion.div
            className="forgery-section col-12 mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="card glass-card h-100">
              <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                  <div>
                    <h3 className="section-title mb-1">Forgery Tests</h3>
                    <p className="text-muted small mb-0">
                      Highlighted because processing remark 20 indicates failed authentication tests.
                    </p>
                  </div>
                  <span className="badge bg-danger-subtle text-danger-emphasis">
                    <i className="bi bi-exclamation-octagon me-1" aria-hidden="true"></i>
                    Remark 20
                  </span>
                </div>
                <div className="forgery-grid" role="list">
                  {flaggedForgeryTests.map((test, idx) => {
                    const result = test.testResult;
                    const showResult = shouldShowTestResult(result);
                    const statusInfo = getForgeryStatusLabel(result);
                    return (
                      <div
                        key={test.path || idx}
                        className="forgery-card forgery-card--alert"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleForgeryCardSelect(test)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleForgeryCardSelect(test);
                          }
                        }}
                        aria-label={`View details for forgery test ${test.subtype || test.type || formatForgeryLocation(test.location)}`}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                          <span className="badge bg-light text-dark">
                            {formatForgeryLocation(test.location)}
                          </span>
                          <span className="text-muted small">{test.type || 'Unknown type'}</span>
                        </div>
                        <h4 className="forgery-card__title mb-1">
                          {test.subtype || 'Unnamed forgery test'}
                        </h4>
                        <p className={`forgery-card__status ${statusInfo.className} mb-2`}>
                          <i className={`bi ${statusInfo.icon} me-1`} aria-hidden="true"></i>
                          {statusInfo.label}
                        </p>
                        {/* Result badge intentionally removed per request */}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Remarks Section */}
      <motion.div className="remarks-section" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
        <h3 className="section-title">Remarks</h3>
        <div className="row">
          <div className="col-md-6">
            <RemarksList
              remarks={processingRemarks}
              title="Processing Remarks"
              type="processing"
              onShowDocumentation={handleShowDocumentation}
            />
          </div>
          <div className="col-md-6">
            <RemarksList
              remarks={riskManagementRemarks}
              title="Risk Management Remarks"
              type="risk"
              onShowDocumentation={handleShowDocumentation}
            />
          </div>
        </div>
      </motion.div>

      {/* Metadata Section */}
      <div className="metadata-section mt-4">
        <h3 className="section-title">Metadata</h3>
        <div className="card glass-card">
          <div className="card-body">
            {renderMetadata()}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {activeForgeryDetail && (
          <motion.div
            className="forgery-detail-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeForgeryDetail}
            role="presentation"
          >
            <motion.div
              className="forgery-detail-card glass-card"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Forgery test details"
            >
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted text-uppercase small mb-1">Forgery Test</p>
                  <h4 className="mb-1">{activeForgeryDetail.title}</h4>
                  {activeForgeryDetail.type && (
                    <span className="badge bg-light text-body-secondary me-2">{activeForgeryDetail.type}</span>
                  )}
                  <span className="badge bg-danger-subtle text-danger-emphasis">
                    {activeForgeryDetail.location}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close forgery test details"
                  onClick={closeForgeryDetail}
                ></button>
              </div>
              <p className="mb-3">
                {activeForgeryDetail.description}
              </p>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleViewInDocs}
                >
                  <i className="bi bi-book me-1" aria-hidden="true"></i>
                  View in Remark Codes
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={closeForgeryDetail}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResultsDisplay;
